/**
 * Matrix questions By Row or By Column (M4S-5618). Spec: docs/pdlc/crosstabs-matrix-by-column.md.
 *
 * By Row is one table per statement with the answer choices as rows; By Column is one table per
 * answer choice with the statements as rows. A question's orientation is its own choice if it has
 * one, else the crosstab-wide default, else By Row.
 *
 * The per-question map is persisted as `perQuestionMatrixTranspose` (true = By Column), a name kept
 * from M4S-5595 so saved records keep their meaning. It holds only differences from the default,
 * on every write and every read: modal saves have been storing a `false` for every matrix question
 * touched, and under the resolution order those would pin a question By Row where the crosstab
 * default could never reach it.
 */

import type { CrosstabQuestionDto } from '@/lib/crosstab/question-formatter';

/** Question types that render as matrix blocks. */
export const MATRIX_KINDS = ['matrix', 'grid', 'intensity scale matrix'] as const;

export function isMatrixKind(kind: string | null | undefined): boolean {
    return !!kind && (MATRIX_KINDS as readonly string[]).includes(kind);
}

/** Draws as matrix blocks: a matrix kind with a non-empty matrixOptions. */
export function isMatrixQuestion<
    T extends Pick<CrosstabQuestionDto, 'questionType' | 'matrixOptions'>,
>(q: T | null | undefined): q is T {
    return !!q && isMatrixKind(q.questionType) && Object.keys(q.matrixOptions ?? {}).length > 0;
}

/**
 * Standard matrix and grid key `matrixOptions` by answer choice (postgres-question-mapper
 * `buildMatrixOptions`); intensity scale matrix keys it by statement
 * (`buildIntensityMatrixOptions`).
 */
export function isChoiceKeyed(kind: string | null | undefined): boolean {
    return kind === 'matrix' || kind === 'grid';
}

/** The question's own choice, else the crosstab default, else By Row. */
export function resolveByColumn(
    qid: string,
    perQuestion: Record<string, boolean> | undefined,
    byColumnDefault: boolean | undefined,
): boolean {
    return perQuestion?.[qid.toUpperCase()] ?? byColumnDefault === true;
}

/**
 * Drops every entry equal to the default and sorts the keys, since the compute dirty check
 * compares JSON. Applied on read (the record and the browser slot) and when the default changes;
 * under this rule, changing the default clears every exception.
 */
export function pruneByColumnOverrides(
    perQuestion: Record<string, boolean> | undefined,
    byColumnDefault: boolean | undefined,
): Record<string, boolean> {
    const fallback = byColumnDefault === true;
    const out: Record<string, boolean> = {};
    for (const qid of Object.keys(perQuestion ?? {}).sort()) {
        const value = perQuestion![qid];
        if (typeof value === 'boolean' && value !== fallback) out[qid] = value;
    }
    return out;
}

/** Writes one question's choice, then prunes. Returns a new map; the qid is upper-cased. */
export function withByColumnChoice(
    perQuestion: Record<string, boolean> | undefined,
    qid: string,
    byColumn: boolean,
    byColumnDefault: boolean | undefined,
): Record<string, boolean> {
    return pruneByColumnOverrides(
        { ...(perQuestion ?? {}), [qid.toUpperCase()]: byColumn },
        byColumnDefault,
    );
}

/**
 * Whether a matrix block's id takes the `:scale:` prefix: `${qid}:scale:${label}` rather than
 * `${qid}:${label}`.
 *
 * Until option edits (M4S-5618, spec §17.3) two producers drew matrices, and the "transposed" one,
 * which made a block per distinct option label, prefixed its ids so. One producer now draws both
 * orientations and keeps both formats, so that no block id changes. A choice-keyed matrix labels
 * its options with statements, so its By Row blocks take the prefix; an intensity scale matrix
 * labels them with scale points, so its By Column blocks do.
 */
export function usesTransposedProducer(
    kind: string | null | undefined,
    byColumn: boolean,
): boolean {
    return isChoiceKeyed(kind) ? !byColumn : byColumn;
}
